import React, { useEffect, useMemo, useState } from 'react';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun, WidthType } from 'docx';
import AIRubricBuilder from './components/AIRubricBuilder.jsx';
import axios from './axiosConfig';
import { saveAs } from 'file-saver';
import { generateRevisionPlan, normalizeAiError, runDiagnostics } from './services/ai.js';
import './AITools.css';


function flattenAiText(value) {
    if (Array.isArray(value)) {
        return value
            .flatMap((item) => {
                if (typeof item === 'string' || typeof item === 'number') {
                    return [String(item)];
                }
                if (Array.isArray(item)) {
                    return [flattenAiText(item)];
                }
                if (item && typeof item === 'object') {
                    return [flattenAiText(Object.values(item))];
                }
                return [];
            })
            .map((item) => String(item || '').trim())
            .filter(Boolean)
            .join(' ');
    }

    if (value && typeof value === 'object') {
        return flattenAiText(Object.values(value));
    }

    if (typeof value === 'number') {
        return String(value);
    }

    return typeof value === 'string' ? value.trim() : '';
}

function ensureText(value, fallback = '-') {
    const text = flattenAiText(value);
    const normalized = text.trim();
    if (!normalized || normalized === 'Array' || normalized === '[object Object]') {
        return fallback;
    }
    return normalized;
}

function ensureList(value, fallback = []) {
    if (!Array.isArray(value)) {
        const text = flattenAiText(value);
        return text ? [text] : fallback;
    }

    const normalized = value
        .map((item) => ensureText(item, ''))
        .filter(Boolean);

    return normalized.length ? normalized : fallback;
}

export default function AITools() {
    const [subjects, setSubjects] = useState([]);
    const [selectedSubjectId, setSelectedSubjectId] = useState('');
    const [selectedTerm, setSelectedTerm] = useState('1');
    const [subjectContext, setSubjectContext] = useState(null);
    const [contextLoading, setContextLoading] = useState(false);
    const [contextError, setContextError] = useState('');
    const [selectedAssessmentKey, setSelectedAssessmentKey] = useState('');
    const [diagnosticsForm, setDiagnosticsForm] = useState({
        taskType: '',
        additionalContext: ''
    });
    const [revisionForm, setRevisionForm] = useState({
        days: 5
    });
    const [diagnosticsResult, setDiagnosticsResult] = useState(null);
    const [revisionResult, setRevisionResult] = useState(null);
    const [diagnosticsError, setDiagnosticsError] = useState('');
    const [revisionError, setRevisionError] = useState('');
    const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
    const [revisionLoading, setRevisionLoading] = useState(false);
    const [showRiskSupport, setShowRiskSupport] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const loadSubjects = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_dashboard.php`);
                if (!isMounted) {
                    return;
                }

                const nextSubjects = Array.isArray(response.data?.subjects) ? response.data.subjects : [];
                setSubjects(nextSubjects);
                let revisionContext = null;
                try {
                    revisionContext = JSON.parse(localStorage.getItem('marksure_ai_revision_context') || 'null');
                } catch {
                    revisionContext = null;
                }

                const requestedSubject = nextSubjects.find((subject) => String(subject.subject_id) === String(revisionContext?.subjectId));
                if (requestedSubject) {
                    setSelectedSubjectId(String(requestedSubject.subject_id));
                    setSelectedTerm(String(revisionContext?.term || '1'));
                    localStorage.removeItem('marksure_ai_revision_context');
                } else if (nextSubjects.length > 0) {
                    setSelectedSubjectId((current) => current || String(nextSubjects[0].subject_id));
                }
            } catch (error) {
                if (isMounted) {
                    setContextError(normalizeAiError(error));
                }
            }
        };

        loadSubjects();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!selectedSubjectId) {
            setSubjectContext(null);
            return;
        }

        let isMounted = true;

        const loadSubjectContext = async () => {
            setContextLoading(true);
            setContextError('');
            setDiagnosticsResult(null);
            setRevisionResult(null);

            try {
                const [subjectRes, sectionsRes, marksRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/fetch_class_marks.php?subject_id=${selectedSubjectId}&term=${selectedTerm}`),
                    axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${selectedSubjectId}&term=${selectedTerm}`),
                    axios.get(`${process.env.REACT_APP_API_URL}/section_marks.php?subject_id=${selectedSubjectId}&term=${selectedTerm}`)
                ]);

                if (!isMounted) {
                    return;
                }

                const subject = {
                    id: selectedSubjectId,
                    name: subjectRes.data?.subject_name || '',
                    grade: String(subjectRes.data?.grade || ''),
                    className: subjectRes.data?.class_name || '',
                    passRequirements: subjectRes.data?.pass_requirements || null,
                    students: Array.isArray(subjectRes.data?.students) ? subjectRes.data.students : []
                };
                const sections = Array.isArray(sectionsRes.data?.sections) ? sectionsRes.data.sections : [];
                const marks = Array.isArray(marksRes.data?.marks) ? marksRes.data.marks : [];
                const assessments = buildAssessmentSummaries(sections, marks);
                const nextAssessmentKey = assessments[0]?.key || '';

                setSubjectContext({ subject, sections, marks, assessments });
                setSelectedAssessmentKey(nextAssessmentKey);
                setDiagnosticsForm({
                    taskType: assessments[0]?.label || '',
                    additionalContext: ''
                });
            } catch (error) {
                if (isMounted) {
                    setSubjectContext(null);
                    setContextError(normalizeAiError(error));
                }
            } finally {
                if (isMounted) {
                    setContextLoading(false);
                }
            }
        };

        loadSubjectContext();
        return () => {
            isMounted = false;
        };
    }, [selectedSubjectId, selectedTerm]);

    const selectedAssessment = useMemo(() => {
        if (!subjectContext) {
            return null;
        }

        return subjectContext.assessments.find((assessment) => assessment.key === selectedAssessmentKey) || subjectContext.assessments[0] || null;
    }, [selectedAssessmentKey, subjectContext]);

    const diagnosticsPayload = useMemo(() => {
        if (!subjectContext || !selectedAssessment) {
            return [];
        }

        return buildDiagnosticsStudents(subjectContext.subject.students, selectedAssessment);
    }, [selectedAssessment, subjectContext]);

    const weakTopics = useMemo(() => {
        if (diagnosticsResult?.weakAreas?.length) {
            return diagnosticsResult.weakAreas.map((item) => item.topic);
        }

        if (!selectedAssessment) {
            return [];
        }

        return selectedAssessment.sectionSummaries
            .filter((section) => section.averagePercentage < 60)
            .map((section) => section.topic);
    }, [diagnosticsResult, selectedAssessment]);

    const supportThreshold = useMemo(() => {
        const requirements = subjectContext?.subject?.passRequirements || {};
        const threshold = Number(requirements.support_threshold ?? requirements.support_threshold_override);
        return Number.isFinite(threshold) ? threshold : 55;
    }, [subjectContext]);

    const handleDiagnosticsSubmit = async (event) => {
        event.preventDefault();
        setDiagnosticsError('');
        setDiagnosticsLoading(true);

        try {
            const result = await runDiagnostics({
                grade: subjectContext?.subject.grade || '',
                subject: subjectContext?.subject.name || '',
                taskType: diagnosticsForm.taskType,
                additionalContext: diagnosticsForm.additionalContext,
                students: diagnosticsPayload,
                passRequirements: subjectContext?.subject.passRequirements || undefined
            });
            setDiagnosticsResult(result);
            setShowRiskSupport(false);
        } catch (error) {
            setDiagnosticsError(normalizeAiError(error));
        } finally {
            setDiagnosticsLoading(false);
        }
    };

    const handleExportDiagnosticsWord = async () => {
        if (!diagnosticsResult || !subjectContext) {
            return;
        }

        const weakAreaRows = [
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Topic', bold: true })] })] }),
                    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Average %', bold: true })] })] }),
                    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Severity', bold: true })] })] }),
                    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Focus', bold: true })] })] })
                ]
            }),
            ...diagnosticsResult.weakAreas.map((area) => new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph(area.topic)] }),
                    new TableCell({ children: [new Paragraph(String(Math.round(area.averagePercentage)))] }),
                    new TableCell({ children: [new Paragraph(area.severity)] }),
                    new TableCell({ children: [new Paragraph(area.recommendedFocus || area.observation)] })
                ]
            }))
        ];

        const makeBullets = (items, formatter) => (
            ensureList(items).length
                ? ensureList(items).map((item) => new Paragraph({
                    children: [new TextRun(ensureText(formatter(item), 'No additional items available.'))],
                    bullet: { level: 0 }
                }))
                : [new Paragraph('No additional items available.')]
        );

        const document = new Document({
            sections: [{
                children: [
                    new Paragraph({
                        heading: 'Heading1',
                        children: [new TextRun(`${subjectContext.subject.name} Diagnostics Report`)]
                    }),
                    new Paragraph({
                        children: [
                            new TextRun({ text: `Grade: ${subjectContext.subject.grade || '-'}`, bold: true }),
                            new TextRun(`   Assessment: ${selectedAssessment?.label || '-'}`),
                            new TextRun(`   Term: ${selectedTerm}`)
                        ]
                    }),
                    diagnosticsForm.additionalContext
                        ? new Paragraph({ text: `Teacher Context: ${diagnosticsForm.additionalContext}` })
                        : new Paragraph(''),
                    new Paragraph({ heading: 'Heading2', text: 'Summary' }),
                    new Paragraph(ensureText(diagnosticsResult.summary, 'No summary available.')),
                    new Paragraph({ heading: 'Heading2', text: 'Headline Findings' }),
                    ...makeBullets(diagnosticsResult.headlineFindings || [], (item) => item),
                    new Paragraph({ heading: 'Heading2', text: 'Cohort Snapshot' }),
                    new Paragraph(`Learners: ${diagnosticsResult.cohortSnapshot?.learnerCount ?? 0}`),
                    new Paragraph(`Class average: ${Math.round(diagnosticsResult.cohortSnapshot?.classAverage ?? 0)}%`),
                    new Paragraph(`Pass rate: ${Math.round(diagnosticsResult.cohortSnapshot?.passRate ?? 0)}%`),
                    new Paragraph({ heading: 'Heading2', text: 'Weak Areas' }),
                    new Table({
                        width: { size: 100, type: WidthType.PERCENTAGE },
                        rows: weakAreaRows
                    }),
                    new Paragraph({ heading: 'Heading2', text: 'At-Risk Students' }),
                    ...makeBullets(diagnosticsResult.atRiskStudents || [], (student) => `${ensureText(student.studentName, 'Learner')}: ${ensureText(student.reason, '')} ${ensureText(student.recommendedAction, '')}`),
                    new Paragraph({ heading: 'Heading2', text: 'Misconception Patterns' }),
                    ...makeBullets(diagnosticsResult.misconceptionPatterns || [], (item) => `${ensureText(item.pattern, 'Pattern')}: ${ensureText(item.likelyCause, '')}`),
                    new Paragraph({ heading: 'Heading2', text: 'Suggested Interventions' }),
                    ...makeBullets(diagnosticsResult.suggestedInterventions || [], (item) => `${ensureText(item.title, 'Intervention')}: ${ensureText(item.action, '')}`),
                    new Paragraph({ heading: 'Heading2', text: 'Assessment Readiness' }),
                    new Paragraph(`Status: ${ensureText(diagnosticsResult.assessmentReadiness?.status, 'Not available')}`),
                    new Paragraph(ensureText(diagnosticsResult.assessmentReadiness?.rationale, '')),
                    new Paragraph({ heading: 'Heading2', text: 'HOD Monitoring Priorities' }),
                    ...makeBullets(diagnosticsResult.hodView?.monitoringPoints || [], (item) => item)
                ]
            }]
        });

        const blob = await Packer.toBlob(document);
        const safeName = `${subjectContext.subject.name}_${selectedAssessment?.label || 'diagnostics'}`
            .replace(/[^a-z0-9]+/gi, '_')
            .replace(/^_+|_+$/g, '');
        saveAs(blob, `${safeName || 'marksure_diagnostics'}.docx`);
    };

    const handleRevisionSubmit = async (event) => {
        event.preventDefault();
        setRevisionError('');
        setRevisionLoading(true);

        try {
            const result = await generateRevisionPlan({
                grade: subjectContext?.subject.grade || '',
                subject: subjectContext?.subject.name || '',
                days: Number(revisionForm.days) || 0,
                weakTopics,
                passRequirements: subjectContext?.subject.passRequirements || undefined
            });
            setRevisionResult(result);
        } catch (error) {
            setRevisionError(normalizeAiError(error));
        } finally {
            setRevisionLoading(false);
        }
    };

    const openRevisionPlan = () => {
        document.getElementById('revision-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleExportRevisionWord = async () => {
        if (!revisionResult || !subjectContext) {
            return;
        }

        const dayParagraphs = revisionResult.days.flatMap((day) => ([
            new Paragraph({
                heading: 'Heading2',
                children: [new TextRun(`Day ${day.day}: ${day.focusTopic}`)]
            }),
            new Paragraph({
                children: [
                    new TextRun({ text: 'Objective: ', bold: true }),
                    new TextRun(day.objective)
                ]
            }),
            new Paragraph({
                children: [
                    new TextRun({ text: 'CAPS focus: ', bold: true }),
                    new TextRun(day.capsFocus)
                ]
            }),
            new Paragraph({
                children: [
                    new TextRun({ text: 'Starter: ', bold: true }),
                    new TextRun(day.starter)
                ]
            }),
            new Paragraph({
                children: [
                    new TextRun({ text: 'Teacher input: ', bold: true }),
                    new TextRun(day.teacherInput)
                ]
            }),
            ...day.activities.map((activity) => new Paragraph({
                children: [new TextRun(activity)],
                bullet: { level: 0 }
            })),
            new Paragraph({
                children: [
                    new TextRun({ text: 'Differentiation: ', bold: true }),
                    new TextRun(day.differentiation)
                ]
            }),
            new Paragraph({
                children: [
                    new TextRun({ text: 'Resources: ', bold: true }),
                    new TextRun(day.resources)
                ]
            }),
            new Paragraph({
                children: [
                    new TextRun({ text: 'Homework: ', bold: true }),
                    new TextRun(day.homework)
                ]
            }),
            new Paragraph({
                children: [
                    new TextRun({ text: 'Informal assessment: ', bold: true }),
                    new TextRun(day.informalAssessment)
                ]
            }),
            new Paragraph({
                children: [
                    new TextRun({ text: 'Exit ticket: ', bold: true }),
                    new TextRun(day.exitTicket)
                ]
            }),
            new Paragraph({ heading: 'Heading3', text: 'Teacher questioning prompts' }),
            ...ensureList(day.teacherQuestions).map((question) => new Paragraph({
                children: [new TextRun(question)],
                bullet: { level: 0 }
            })),
            new Paragraph({ heading: 'Heading3', text: 'Quick assessment with memo' }),
            ...ensureList(day.assessmentItems).flatMap((item, index) => ([
                new Paragraph({
                    children: [new TextRun({ text: `${index + 1}. ${ensureText(item.question, '')} (${item.marks || 1})`, bold: true })]
                }),
                new Paragraph({
                    children: [new TextRun({ text: 'Memo: ', bold: true }), new TextRun(ensureText(item.memo, ''))]
                }),
                new Paragraph({
                    children: [new TextRun({ text: 'Cognitive level: ', bold: true }), new TextRun(ensureText(item.cognitiveLevel, ''))]
                })
            ]))
        ]));

        const document = new Document({
            sections: [{
                children: [
                    new Paragraph({
                        heading: 'Heading1',
                        children: [new TextRun(revisionResult.planTitle)]
                    }),
                    new Paragraph({
                        children: [
                            new TextRun({ text: `Grade: ${revisionResult.overview?.grade || subjectContext.subject.grade || '-'}`, bold: true }),
                            new TextRun(`   Subject: ${revisionResult.overview?.subject || subjectContext.subject.name || '-'}`),
                            new TextRun(`   Term: ${selectedTerm}`)
                        ]
                    }),
                    new Paragraph({
                        children: [
                            new TextRun({ text: 'Assessment source: ', bold: true }),
                            new TextRun(selectedAssessment?.label || 'Selected assessment')
                        ]
                    }),
                    new Paragraph({
                        children: [
                            new TextRun({ text: 'CAPS alignment: ', bold: true }),
                            new TextRun(revisionResult.overview?.capsAlignment || '')
                        ]
                    }),
                    new Paragraph(revisionResult.overview?.purpose || ''),
                    ...dayParagraphs,
                    new Paragraph({ heading: 'Heading2', text: 'Teacher Moves' }),
                    ...(revisionResult.teacherMoves?.length
                        ? revisionResult.teacherMoves.map((item) => new Paragraph({
                            children: [new TextRun(item)],
                            bullet: { level: 0 }
                        }))
                        : [new Paragraph('No teacher moves provided.')]),
                    new Paragraph({ heading: 'Heading2', text: 'Success Metrics' }),
                    ...(revisionResult.successMetrics?.length
                        ? revisionResult.successMetrics.map((item) => new Paragraph({
                            children: [new TextRun(item)],
                            bullet: { level: 0 }
                        }))
                        : [new Paragraph('No success metrics provided.')]),
                    ...(revisionResult.capsReferences?.length
                        ? [
                            new Paragraph({ heading: 'Heading2', text: 'Official CAPS References' }),
                            ...revisionResult.capsReferences.map((reference) => new Paragraph({
                                children: [
                                    new TextRun({ text: `${reference.title}: `, bold: true }),
                                    new TextRun(`${reference.url} - ${reference.relevance}`)
                                ],
                                bullet: { level: 0 }
                            }))
                        ]
                        : [])
                ]
            }]
        });

        const blob = await Packer.toBlob(document);
        const safeName = `${subjectContext.subject.name}_${selectedAssessment?.label || 'revision_plan'}`
            .replace(/[^a-z0-9]+/gi, '_')
            .replace(/^_+|_+$/g, '');
        saveAs(blob, `${safeName || 'marksure_revision_plan'}.docx`);
    };

    return (
        <div className="ai-tools-page">
            <section className="ai-tools-hero">
                <div>
                    <p className="ai-tools-kicker">AI Workspace</p>
                    <h1>Rubrics, diagnostics, and revision plans in one place</h1>
                    <p className="ai-tools-intro">
                        Choose a real subject and term first. Every AI output on this page is generated from the assessments and marks already captured in Marksure.
                    </p>
                </div>
            </section>

            <section className="ai-tools-toolbar">
                <div className="ai-tools-grid">
                    <label>
                        Subject
                        <select
                            value={selectedSubjectId}
                            onChange={(event) => setSelectedSubjectId(event.target.value)}
                        >
                            {subjects.length === 0 && <option value="">No subjects available</option>}
                            {subjects.map((subject) => (
                                <option key={subject.subject_id} value={subject.subject_id}>
                                    {subject.name} | Grade {subject.grade}{subject.class_name ? ` | ${subject.class_name}` : ''}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Term
                        <select
                            value={selectedTerm}
                            onChange={(event) => setSelectedTerm(event.target.value)}
                        >
                            <option value="1">Term 1</option>
                            <option value="2">Term 2</option>
                            <option value="3">Term 3</option>
                            <option value="4">Term 4</option>
                        </select>
                    </label>
                    <label>
                        Assessment
                        <select
                            value={selectedAssessmentKey}
                            onChange={(event) => {
                                setSelectedAssessmentKey(event.target.value);
                                const next = subjectContext?.assessments.find((item) => item.key === event.target.value);
                                setDiagnosticsForm((current) => ({ ...current, taskType: next?.label || '' }));
                            }}
                            disabled={!subjectContext || subjectContext.assessments.length === 0}
                        >
                            {subjectContext?.assessments?.length
                                ? subjectContext.assessments.map((assessment) => (
                                    <option key={assessment.key} value={assessment.key}>
                                        {assessment.label} | {assessment.totalMarks} marks
                                    </option>
                                ))
                                : <option value="">No assessments found</option>}
                        </select>
                    </label>
                </div>
                {contextLoading && <div className="ai-tools-info">Loading subject data...</div>}
                {contextError && <div className="ai-tools-error">{contextError}</div>}
                {subjectContext && selectedAssessment && (
                    <div className="ai-tools-context-card">
                        <div>
                            <strong>{subjectContext.subject.name}</strong>
                            <span> Grade {subjectContext.subject.grade}{subjectContext.subject.className ? ` | ${subjectContext.subject.className}` : ''}</span>
                        </div>
                        <div>
                            <strong>{selectedAssessment.label}</strong>
                            <span> {selectedAssessment.sections.length} section{selectedAssessment.sections.length === 1 ? '' : 's'} | {selectedAssessment.totalMarks} total marks | {diagnosticsPayload.length} learners</span>
                        </div>
                    </div>
                )}
            </section>

            <div className="ai-tools-layout">
                <section className="ai-tools-panel">
                    <AIRubricBuilder
                        defaultGrade={subjectContext?.subject.grade || ''}
                        defaultSubject={subjectContext?.subject.name || ''}
                        defaultTaskType={selectedAssessment?.label || ''}
                        defaultTotalMarks={selectedAssessment?.totalMarks || 50}
                    />
                    {selectedAssessment && (
                        <div className="ai-tools-result">
                            <h3>Assessment context for rubric generation</h3>
                            <ul>
                                {selectedAssessment.sections.map((section) => (
                                    <li key={section.id}>
                                        <strong>{section.section_name}</strong>: {section.max_mark} marks
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>

                <section className="ai-tools-panel">
                    <div className="ai-tools-panel__header">
                        <h2>Diagnostics</h2>
                        <p>Analyze the selected assessment using the recorded marks and turn the output into a proper intervention dashboard.</p>
                    </div>
                    <form onSubmit={handleDiagnosticsSubmit} className="ai-tools-form">
                        <div className="ai-tools-grid">
                            <label>
                                Task Type
                                <input
                                    value={diagnosticsForm.taskType}
                                    onChange={(event) => setDiagnosticsForm((current) => ({ ...current, taskType: event.target.value }))}
                                    placeholder="Assessment name"
                                />
                            </label>
                        </div>
                        <label>
                            Teacher Context
                            <textarea
                                rows={3}
                                value={diagnosticsForm.additionalContext}
                                onChange={(event) => setDiagnosticsForm((current) => ({ ...current, additionalContext: event.target.value }))}
                                placeholder="Add context like learner barriers, language level, accommodations, class trends, or what you want the analysis to focus on."
                            />
                        </label>
                        <div className="ai-tools-preview">
                            <h3>Students included</h3>
                            <p>{diagnosticsPayload.length} learners will be analyzed from recorded section marks.</p>
                            <ul>
                                {diagnosticsPayload.slice(0, 6).map((student) => (
                                    <li key={student.studentName}>
                                        <strong>{student.studentName}</strong> {student.overallPercentage != null ? `| ${Math.round(student.overallPercentage)}%` : ''}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={diagnosticsLoading}>
                            {diagnosticsLoading ? 'Running Diagnostics...' : 'Run Diagnostics'}
                        </button>
                        {diagnosticsLoading && (
                            <div className="ai-tools-loading-banner" role="status" aria-live="polite">
                                <span className="ai-tools-loading-spinner" aria-hidden="true" />
                                <div>
                                    <strong>Diagnostics are still loading.</strong>
                                    <p>Please wait while MarkSure analyzes the assessment data. The button is locked until the response comes back.</p>
                                </div>
                            </div>
                        )}
                    </form>
                    {diagnosticsError && <div className="ai-tools-error">{diagnosticsError}</div>}
                    {diagnosticsResult && (
                        <div className="ai-tools-result ai-tools-diagnostics">
                            <div className="ai-tools-result__actions">
                                <button type="button" className="btn btn-secondary" onClick={handleExportDiagnosticsWord}>
                                    Save Diagnostics as Word
                                </button>
                            </div>

                            <div className="ai-tools-summary-card">
                                <p className="ai-tools-section-kicker">Executive Summary</p>
                                <h3>Diagnostic overview for {selectedAssessment?.label}</h3>
                                <p>{ensureText(diagnosticsResult.summary, 'No summary available.')}</p>
                            </div>

                            {diagnosticsResult.headlineFindings?.length > 0 && (
                                <>
                                    <h3>Headline Findings</h3>
                                    <ul className="ai-tools-clean-list">
                                        {ensureList(diagnosticsResult.headlineFindings).map((item, index) => (
                                            <li key={`finding-${index}`}>{ensureText(item)}</li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            <h3>Cohort Snapshot</h3>
                            <div className="ai-tools-stat-grid ai-tools-stat-grid--compact">
                                <article className="ai-tools-stat-card">
                                    <span>Learners</span>
                                    <strong>{diagnosticsResult.cohortSnapshot?.learnerCount ?? 0}</strong>
                                </article>
                                <article className="ai-tools-stat-card">
                                    <span>Class Average</span>
                                    <strong>{formatPercent(diagnosticsResult.cohortSnapshot?.classAverage)}</strong>
                                </article>
                                <article className="ai-tools-stat-card">
                                    <span>Pass Rate</span>
                                    <strong>{formatPercent(diagnosticsResult.cohortSnapshot?.passRate)}</strong>
                                </article>
                                <article className="ai-tools-stat-card">
                                    <span>Below 50%</span>
                                    <strong>{diagnosticsResult.cohortSnapshot?.below50Count ?? 0}</strong>
                                </article>
                                <article className="ai-tools-stat-card">
                                    <span>70% And Above</span>
                                    <strong>{diagnosticsResult.cohortSnapshot?.atOrAbove70Count ?? 0}</strong>
                                </article>
                            </div>

                            {diagnosticsResult.performanceBands?.length > 0 && (
                                <>
                                    <h3>Performance Bands</h3>
                                    <div className="ai-tools-table-wrap">
                                        <table className="ai-tools-table">
                                            <thead>
                                                <tr>
                                                    <th>Band</th>
                                                    <th>Learners</th>
                                                    <th>Meaning</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {diagnosticsResult.performanceBands.map((band, index) => (
                                                    <tr key={`band-${index}`}>
                                                        <td>{band.label}</td>
                                                        <td>{band.learnerCount}</td>
                                                        <td>{band.description}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            <h3>Weak Areas</h3>
                            <div className="ai-tools-table-wrap">
                                <table className="ai-tools-table">
                                    <thead>
                                        <tr>
                                            <th>Topic</th>
                                            <th>Average</th>
                                            <th>Severity</th>
                                            <th>Observation</th>
                                            <th>Recommended Focus</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {diagnosticsResult.weakAreas.map((area, index) => (
                                            <tr key={`${area.topic}-${index}`}>
                                                <td>{area.topic}</td>
                                                <td>{formatPercent(area.averagePercentage)}</td>
                                                <td><span className={`ai-tools-badge ai-tools-badge--${area.severity}`}>{area.severity}</span></td>
                                                <td>{area.observation}</td>
                                                <td>{area.recommendedFocus}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {diagnosticsResult.strengthAreas?.length > 0 && (
                                <>
                                    <h3>Strength Areas</h3>
                                    <ul className="ai-tools-clean-list">
                                        {diagnosticsResult.strengthAreas.map((area, index) => (
                                            <li key={`${area.topic}-strength-${index}`}>
                                        <strong>{ensureText(area.topic, 'Topic')}</strong> ({formatPercent(area.averagePercentage)}): {ensureText(area.insight, '-')}
                                    </li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            <h3>At-Risk Learners (support at or below {supportThreshold}%)</h3>
                            {diagnosticsResult.atRiskStudents?.length > 0 ? (
                                <div className="ai-tools-table-wrap">
                                    <table className="ai-tools-table">
                                        <thead>
                                            <tr>
                                                <th>Learner</th>
                                                <th>Risk</th>
                                                <th>Reason</th>
                                                <th>Support Priority</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {diagnosticsResult.atRiskStudents.map((student, index) => (
                                                <tr key={`${student.studentName}-${index}`}>
                                                    <td>{student.studentName}</td>
                                                    <td><span className={`ai-tools-badge ai-tools-badge--${student.riskLevel === 'high' ? 'high' : 'medium'}`}>{student.riskLevel}</span></td>
                                                    <td>{ensureText(student.reason, '-')}</td>
                                                    <td>{ensureText(student.supportPriority, '-')}</td>
                                                    <td>{ensureText(student.recommendedAction, '-')}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="ai-tools-muted">No at-risk learners were flagged from the current assessment data.</p>
                            )}

                            <div className="ai-tools-risk-actions">
                                <button type="button" className="ai-tools-risk-toggle" onClick={() => setShowRiskSupport((current) => !current)} aria-expanded={showRiskSupport}>
                                    {showRiskSupport ? 'Hide suggested interventions and resources' : 'Show suggested interventions and resources'}
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={openRevisionPlan}>
                                    Open Revision Plan
                                </button>
                            </div>
                            {showRiskSupport && <div className="ai-tools-risk-support">
                                <div>
                                    <h4>Suggested Interventions</h4>
                                    <ul className="ai-tools-clean-list">
                                        {ensureList(diagnosticsResult.suggestedInterventions).map((item, index) => (
                                            <li key={`risk-intervention-${index}`}><strong>{ensureText(item.title, 'Intervention')}:</strong> {ensureText(item.action, '-')}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h4>Resource Priorities</h4>
                                    <ul className="ai-tools-clean-list">
                                        {ensureList(diagnosticsResult.hodView?.resourcePriorities).map((item, index) => <li key={`risk-resource-${index}`}>{ensureText(item, '-')}</li>)}
                                    </ul>
                                </div>
                            </div>}

                            {diagnosticsResult.misconceptionPatterns?.length > 0 && (
                                <>
                                    <h3>Misconception Patterns</h3>
                                    <ul className="ai-tools-clean-list">
                                        {diagnosticsResult.misconceptionPatterns.map((item, index) => (
                                            <li key={`misconception-${index}`}>
                                                <strong>{ensureText(item.pattern, 'Pattern')}:</strong> {ensureText(item.likelyCause, '-')} Evidence: {ensureText(item.evidence, '-')} Response: {ensureText(item.recommendedResponse, '-')}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            <h3>Interventions</h3>
                            <ul className="ai-tools-clean-list">
                                {diagnosticsResult.suggestedInterventions.map((item, index) => (
                                    <li key={`${item.title}-${index}`}>
                                        <strong>{ensureText(item.title, 'Intervention')}:</strong> {ensureText(item.action, '-')}
                                        {ensureList(item.targetTopics).length > 0 ? ` Target topics: ${ensureList(item.targetTopics).join(', ')}.` : ''}
                                        {` Timeframe: ${ensureText(item.timeframe, 'Not specified')}. Best for: ${ensureText(item.suitableFor, 'Not specified')}.`}
                                    </li>
                                ))}
                            </ul>

                            <h3>Assessment Readiness</h3>
                            <div className="ai-tools-summary-card ai-tools-summary-card--soft">
                                <p><strong>Status:</strong> {ensureText(diagnosticsResult.assessmentReadiness?.status, 'Not available')}</p>
                                <p>{ensureText(diagnosticsResult.assessmentReadiness?.rationale, '-')}</p>
                                <ul>
                                    {ensureList(diagnosticsResult.assessmentReadiness?.nextSteps).map((step, index) => (
                                        <li key={`next-step-${index}`}>{step}</li>
                                    ))}
                                </ul>
                            </div>

                            <h3>HOD View</h3>
                            <div className="ai-tools-detail-grid ai-tools-detail-grid--simple">
                                <article className="ai-tools-detail-card">
                                    <h4>Monitoring Points</h4>
                                    <ul>
                                        {ensureList(diagnosticsResult.hodView?.monitoringPoints).map((item, index) => (
                                            <li key={`monitor-${index}`}>{ensureText(item)}</li>
                                        ))}
                                    </ul>
                                </article>
                                <article className="ai-tools-detail-card">
                                    <h4>Resource Priorities</h4>
                                    <ul>
                                        {ensureList(diagnosticsResult.hodView?.resourcePriorities).map((item, index) => (
                                            <li key={`resource-${index}`}>{ensureText(item)}</li>
                                        ))}
                                    </ul>
                                </article>
                                <article className="ai-tools-detail-card">
                                    <h4>Reporting Note</h4>
                                    <p>{ensureText(diagnosticsResult.hodView?.reportingNote, '-')}</p>
                                </article>
                            </div>

                            {diagnosticsResult.capsReferences?.length > 0 && (
                                <>
                                    <h3>Official CAPS References</h3>
                                    <div className="ai-tools-detail-grid ai-tools-detail-grid--simple">
                                        {diagnosticsResult.capsReferences.map((reference, index) => (
                                            <article key={`caps-diagnostic-${index}`} className="ai-tools-detail-card">
                                                <h4>{reference.title}</h4>
                                                <p>{reference.relevance}</p>
                                                <a href={reference.url} target="_blank" rel="noreferrer">{reference.url}</a>
                                            </article>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </section>

                <section id="revision-plan" className="ai-tools-panel">
                    <div className="ai-tools-panel__header">
                        <h2>Revision Plan</h2>
                        <p>Build a grade-bounded intervention plan with teacher prompts, quick assessment questions, memos, marks, and cognitive levels from the weak areas found in this subject&apos;s assessment data.</p>
                    </div>
                    <form onSubmit={handleRevisionSubmit} className="ai-tools-form">
                        <div className="ai-tools-grid">
                            <label>
                                Days
                                <input
                                    type="number"
                                    min="1"
                                    value={revisionForm.days}
                                    onChange={(event) => setRevisionForm((current) => ({ ...current, days: event.target.value }))}
                                />
                            </label>
                        </div>
                        <div className="ai-tools-preview">
                            <h3>Weak topics to use</h3>
                            {weakTopics.length > 0 ? (
                                <ul>
                                    {weakTopics.map((topic) => (
                                        <li key={topic}>{topic}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p>Run diagnostics first, or select an assessment with low-performing sections.</p>
                            )}
                        </div>
                        <div className="ai-tools-preview">
                            <h3>How this plan is built</h3>
                            <p>
                                MarkSure first looks at the selected subject, term, and assessment. It uses the recorded section marks to identify weak topics,
                                then builds a day-by-day intervention flow around those priorities. Each day includes teacher questioning prompts and a marked quick assessment. If diagnostics has already run, the plan uses those returned weak areas first.
                            </p>
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={revisionLoading}>
                            {revisionLoading ? 'Building Plan...' : 'Generate Revision Plan'}
                        </button>
                        {revisionLoading && (
                            <div className="ai-tools-loading-banner" role="status" aria-live="polite">
                                <span className="ai-tools-loading-spinner" aria-hidden="true" />
                                <div>
                                    <strong>Revision plan is still loading.</strong>
                                    <p>Please wait while the CAPS-aligned plan is being prepared. The button is locked until the response is ready.</p>
                                </div>
                            </div>
                        )}
                    </form>
                    {revisionError && <div className="ai-tools-error">{revisionError}</div>}
                    {revisionResult && (
                        <div className="ai-tools-result">
                            <div className="ai-tools-result__actions">
                                <button type="button" className="btn btn-secondary" onClick={handleExportRevisionWord}>
                                    Save Revision Plan as Word
                                </button>
                            </div>
                            <div className="ai-tools-summary-card">
                                <p className="ai-tools-section-kicker">CAPS Revision Plan</p>
                                <h3>{revisionResult.planTitle}</h3>
                                <p>{revisionResult.overview?.purpose}</p>
                                <div className="ai-tools-stat-grid">
                                    <article className="ai-tools-stat-card">
                                        <span>Grade</span>
                                        <strong>{revisionResult.overview?.grade || subjectContext?.subject.grade || '-'}</strong>
                                    </article>
                                    <article className="ai-tools-stat-card">
                                        <span>Subject</span>
                                        <strong>{revisionResult.overview?.subject || subjectContext?.subject.name || '-'}</strong>
                                    </article>
                                    <article className="ai-tools-stat-card">
                                        <span>Days</span>
                                        <strong>{revisionResult.overview?.days || revisionResult.days.length}</strong>
                                    </article>
                                </div>
                                <p><strong>CAPS alignment:</strong> {revisionResult.overview?.capsAlignment}</p>
                            </div>

                            <div className="ai-tools-days">
                                {revisionResult.days.map((day) => (
                                    <article key={day.day} className="ai-tools-day-card">
                                        <div className="ai-tools-topic-card__top">
                                            <div>
                                                <p className="ai-tools-day-label">Day {day.day}</p>
                                                <h4>{day.focusTopic}</h4>
                                            </div>
                                            <span className="ai-tools-badge ai-tools-badge--outline">Revision</span>
                                        </div>
                                        <p><strong>Objective:</strong> {day.objective}</p>
                                        <p><strong>CAPS focus:</strong> {day.capsFocus}</p>
                                        <p><strong>Starter:</strong> {day.starter}</p>
                                        <p><strong>Teacher input:</strong> {day.teacherInput}</p>
                                        <div className="ai-tools-activity-block">
                                            <h5>Activities</h5>
                                            <ul>
                                                {day.activities.map((activity, index) => (
                                                    <li key={`${day.day}-activity-${index}`}>{activity}</li>
                                                ))}
                                            </ul>
                                        </div>
                                        <p><strong>Differentiation:</strong> {day.differentiation}</p>
                                        <p><strong>Resources:</strong> {day.resources}</p>
                                        <p><strong>Homework:</strong> {day.homework}</p>
                                        <p><strong>Informal assessment:</strong> {day.informalAssessment}</p>
                                        <p><strong>Exit ticket:</strong> {day.exitTicket}</p>
                                        <div className="ai-tools-activity-block">
                                            <h5>Teacher questioning prompts</h5>
                                            <ul>
                                                {ensureList(day.teacherQuestions).map((question, index) => (
                                                    <li key={`${day.day}-teacher-question-${index}`}>{question}</li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="ai-tools-activity-block">
                                            <h5>Quick assessment and memo</h5>
                                            {ensureList(day.assessmentItems).map((item, index) => (
                                                <article key={`${day.day}-assessment-item-${index}`} className="ai-tools-detail-card">
                                                    <p><strong>{index + 1}. {ensureText(item.question, '-')}</strong> [{item.marks || 1}]</p>
                                                    <p><strong>Memo:</strong> {ensureText(item.memo, '-')}</p>
                                                    <p><strong>Cognitive level:</strong> {ensureText(item.cognitiveLevel, '-')}</p>
                                                </article>
                                            ))}
                                        </div>
                                    </article>
                                ))}
                            </div>

                            {revisionResult.teacherMoves?.length > 0 && (
                                <>
                                    <h3>Teacher Moves</h3>
                                    <ul className="ai-tools-clean-list">
                                        {revisionResult.teacherMoves.map((item, index) => (
                                            <li key={`teacher-move-${index}`}>{item}</li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            {revisionResult.capsReferences?.length > 0 && (
                                <>
                                    <h3>Official CAPS References</h3>
                                    <div className="ai-tools-detail-grid">
                                        {revisionResult.capsReferences.map((reference, index) => (
                                            <article key={`caps-plan-${index}`} className="ai-tools-detail-card">
                                                <h4>{reference.title}</h4>
                                                <p>{reference.relevance}</p>
                                                <a href={reference.url} target="_blank" rel="noreferrer">{reference.url}</a>
                                            </article>
                                        ))}
                                    </div>
                                </>
                            )}

                            {revisionResult.successMetrics?.length > 0 && (
                                <>
                                    <h3>Success Metrics</h3>
                                    <ul className="ai-tools-clean-list">
                                        {revisionResult.successMetrics.map((metric, index) => (
                                            <li key={`metric-${index}`}>{metric}</li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

function buildAssessmentSummaries(sections, marks) {
    const groups = new Map();
    const marksByStudent = {};

    marks.forEach((mark) => {
        const studentId = String(mark.student_id);
        if (!marksByStudent[studentId]) {
            marksByStudent[studentId] = {};
        }
        marksByStudent[studentId][String(mark.section_id)] = Number(mark.mark);
    });

    sections.forEach((section) => {
        const label = String(section.assessment_group || section.category || section.section_name || 'Assessment').trim();
        const key = label.toLowerCase();
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                label,
                sections: [],
                totalMarks: 0
            });
        }

        const group = groups.get(key);
        group.sections.push(section);
        group.totalMarks += Number(section.max_mark) || 0;
    });

    return Array.from(groups.values()).map((group) => {
        const sectionSummaries = group.sections.map((section) => {
            const sectionMarks = marks
                .filter((mark) => String(mark.section_id) === String(section.id))
                .map((mark) => Number(mark.mark))
                .filter((mark) => Number.isFinite(mark));

            const averageRaw = sectionMarks.length
                ? sectionMarks.reduce((sum, value) => sum + value, 0) / sectionMarks.length
                : 0;

            return {
                sectionId: section.id,
                topic: section.section_name,
                averagePercentage: Number(section.max_mark) > 0 ? (averageRaw / Number(section.max_mark)) * 100 : 0
            };
        });

        return {
            ...group,
            sectionSummaries,
            marksByStudent
        };
    });
}

function buildDiagnosticsStudents(students, assessment) {
    return students.map((student) => {
        const topicMarks = assessment.sections.map((section) => {
            const rawMark = assessment.marksByStudent?.[String(student.student_id)]?.[String(section.id)];

            return {
                topic: section.section_name,
                score: Number.isFinite(rawMark) ? rawMark : 0,
                total: Number(section.max_mark) || 0
            };
        });

        return {
            studentId: student.student_id,
            studentName: `${student.first_name} ${student.last_name}`.trim(),
            overallPercentage: student.average != null ? Number(student.average) : undefined,
            topicMarks
        };
    }).filter((student) => {
        const hasMarks = student.topicMarks.some((topic) => topic.score > 0);
        return hasMarks || student.overallPercentage != null;
    });
}

function formatPercent(value) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? `${Math.round(numeric)}%` : '-';
}
